import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { useMe } from '@/lib/session';
import { cn } from '@/lib/utils';

/**
 * The Admin group's sub-navigation (PART 15.0: Users, Custom Roles,
 * Permissions — the last two open the SAME screen, the prototype quirk kept).
 *
 * BILLING (ADM-07) IS THE FOURTH, and it is the one tab in the product that is
 * not drawn for everybody who can see the strip. §3: "Main menu › Admin ›
 * Billing — the Owner alone (BR-PERM-05, BR-PERM-69). THE MENU ENTRY ITSELF IS
 * ABSENT for everybody else, admins included." So it is absent and not greyed,
 * which is BR-PERM-19's treatment of a menu section rather than BR-PERM-17's
 * treatment of a button — and D-8 notes the spec never settles which applies
 * to a tab. This is a menu entry the spec calls a menu entry, so it follows
 * the menu rule and the spec's own word "absent".
 *
 * Hiding it is a display choice and never the control: `route-permissions.ts`
 * refuses a typed address and the server refuses the request.
 */
const TABS = [
  { to: '/admin', end: true, key: 'admin.tabUsers' },
  { to: '/admin/roles', end: false, key: 'admin.tabRoles' },
  { to: '/admin/roles', end: false, key: 'admin.tabPermissions', duplicate: true },
  { to: '/admin/billing', end: false, key: 'billing.title', ownerOnly: true },
] as const;

export function AdminTabs() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const tabs = TABS.filter((tab) => !('ownerOnly' in tab && tab.ownerOnly) || me?.user.isOwner === true);
  return (
    <nav className="flex flex-wrap gap-1 border-b pb-px" aria-label={t('shell.navAdmin')}>
      {tabs.map((tab) => (
        <NavLink
          key={tab.key}
          to={tab.to}
          end={tab.end}
          className={({ isActive }) =>
            cn(
              'rounded-t-md px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground',
              isActive && !('duplicate' in tab && tab.duplicate) && 'border-b-2 border-primary text-foreground',
            )
          }
        >
          {t(tab.key)}
        </NavLink>
      ))}
    </nav>
  );
}
